// src/hooks/useThemeDownload.js - Hook 의존성 최적화 완료
// 🔧 [수정] Timestamp 추가 및 상태 검증 강화
import { errorLog, debugLog } from '../utils/environment';
import { useEffect, useCallback, useRef } from 'react';
import { useThemeDownloadContext } from '../contexts/ThemeDownloadContext';
import imageLoader from '../services/imageLoader';

const useThemeDownload = (themeId, startStageId, endStageId) => {
 const { 
  downloadStatus: contextDownloadStatus,
  updateDownloadStatus,
  updateDownloadProgress,
  downloadProgress: contextDownloadProgress,
  DOWNLOAD_STATUS_KEY
 } = useThemeDownloadContext();
  
  // [🎯 최적화] 초기화 완료 플래그를 useRef로 관리하여 리렌더링 방지
  const isInitializedRef = useRef(false);
  
  // [🔧 수정] 상태 가져오기 - 새로운 구조 처리
  const getActualStatus = (statusData) => {
    if (!statusData) return 'not-downloaded';
    
    // 하위 호환성: 문자열과 객체 형식 모두 처리
    if (typeof statusData === 'string') {
      return statusData;
    } else if (statusData && typeof statusData === 'object') {
      return statusData.status;
    }
    
    return 'not-downloaded';
  };
  
  // [수정] AppContext의 상태만 사용 (중복 상태 제거)
  const currentStatusData = contextDownloadStatus[themeId];
  const currentStatus = getActualStatus(currentStatusData);
  const currentProgress = contextDownloadProgress[themeId] || 0;
  
  // [🔧 개선] 초기 상태 설정 및 검증
  useEffect(() => {
    // [🔧 수정] 상태 검증 로직 추가
    if (!isInitializedRef.current) {
      // downloading 상태인 경우 실제 다운로드 여부 확인
      if (currentStatus === 'downloading') {
        const isActuallyDownloading = imageLoader.isActuallyDownloading && 
                                      imageLoader.isActuallyDownloading(themeId);
        
        if (!isActuallyDownloading) {
          debugLog('[useThemeDownload] 잘못된 downloading 상태 감지 - 리셋:', themeId);
          updateDownloadStatus(themeId, 'not-downloaded');
        } else {
          debugLog('[useThemeDownload] 실제 다운로드 진행 중 확인:', themeId);
        }
      }
      
      // [🎯 최적화] localStorage 확인 (한 번만 실행)
      if (!contextDownloadStatus[themeId]) {
        try {
          const saved = localStorage.getItem(DOWNLOAD_STATUS_KEY);
          if (saved) {
            const savedStatus = JSON.parse(saved);
            if (savedStatus[themeId]) {
              debugLog('[useThemeDownload] localStorage에서 상태 복원:', {
                themeId,
                status: savedStatus[themeId]
              });
              
              // [🔧 수정] 복원된 상태도 검증
              const savedActualStatus = getActualStatus(savedStatus[themeId]);
              if (savedActualStatus === 'downloading') {
                const isActuallyDownloading = imageLoader.isActuallyDownloading && 
                                              imageLoader.isActuallyDownloading(themeId);
                
                if (!isActuallyDownloading) {
                  debugLog('[useThemeDownload] 복원된 downloading 상태 무효 - 리셋');
                  updateDownloadStatus(themeId, 'not-downloaded');
                } else {
                  updateDownloadStatus(themeId, savedActualStatus);
                }
              } else {
                updateDownloadStatus(themeId, savedActualStatus);
              }
            }
          }
        } catch (error) {
          errorLog('[useThemeDownload] localStorage 읽기 실패:', error);
        }
      }
      
      // [🎯 최적화] 초기화 완료 플래그 설정
      isInitializedRef.current = true;
      debugLog('[useThemeDownload] 초기화 완료:', themeId);
    }
  }, [DOWNLOAD_STATUS_KEY, themeId, currentStatus, contextDownloadStatus, updateDownloadStatus]); 
  
  // [🎯 최적화] 다운로드 시작 함수 - 의존성 최적화 및 상태 체크 강화
  const startDownload = useCallback(async () => {
    // [🎯 최적화] 현재 상태를 직접 확인하여 불필요한 다운로드 방지
    const latestStatusData = contextDownloadStatus[themeId];
    const latestStatus = getActualStatus(latestStatusData);
    
    if (latestStatus === 'downloading') {
      // [🔧 추가] 실제로 다운로드 중인지 다시 한 번 확인
      const isActuallyDownloading = imageLoader.isActuallyDownloading && 
                                    imageLoader.isActuallyDownloading(themeId);
      
      if (isActuallyDownloading) {
        debugLog('[useThemeDownload] 다운로드 스킵 - 실제로 진행 중:', {
          themeId,
          status: latestStatus
        });
        return;
      } else {
        debugLog('[useThemeDownload] 잘못된 downloading 상태 - 재시작:', themeId);
        // 상태 리셋 후 계속 진행
        updateDownloadStatus(themeId, 'not-downloaded');
      }
    }
    
    if (latestStatus === 'downloaded') {
      debugLog('[useThemeDownload] 다운로드 스킵 - 이미 완료됨:', {
        themeId,
        status: latestStatus
      });
      return;
    }
    
    debugLog('[useThemeDownload] 다운로드 시작:', {
      themeId,
      startStageId,
      endStageId,
      currentStatus: latestStatus
    });
    
    // [🔧 수정] 다운로드 상태 업데이트 - timestamp 포함
    updateDownloadStatus(themeId, 'downloading');
    updateDownloadProgress(themeId, 0);
    
    try {
      // [수정] downloadThemeImages를 downloadTheme으로 변경
      // stageIds 배열 생성 제거하고 직접 파라미터 전달
      await imageLoader.downloadTheme(
        themeId,
        startStageId,
        endStageId,
        (progress) => {
          debugLog('[useThemeDownload] 다운로드 진행률:', {
            themeId,
            progress: Math.round(progress)
          });
          updateDownloadProgress(themeId, progress);
        }
      );
      
      debugLog('[useThemeDownload] 다운로드 완료:', themeId);
      updateDownloadStatus(themeId, 'downloaded');
      updateDownloadProgress(themeId, 100);
      
    } catch (error) {
      errorLog('[useThemeDownload] 다운로드 실패:', error);
      updateDownloadStatus(themeId, 'failed');
      updateDownloadProgress(themeId, 0);
      
      // [🎯 최적화] 실패 시 다시 시도할 수 있도록 초기화 플래그 리셋
      isInitializedRef.current = false;
    }
  }, [
    themeId, 
    startStageId, 
    endStageId, 
    contextDownloadStatus, // [🎯 최적화] 실제 상태 확인을 위해 추가
    updateDownloadStatus, 
    updateDownloadProgress
  ]);
  
  // [🔧 추가] 다운로드 재시도 함수
  const retryDownload = useCallback(async () => {
    debugLog('[useThemeDownload] 다운로드 재시도:', themeId);
    
    // 상태 초기화
    updateDownloadStatus(themeId, 'not-downloaded');
    updateDownloadProgress(themeId, 0);
    
    // 약간의 지연 후 다시 시작
    setTimeout(() => {
      startDownload();
    }, 100);
  }, [themeId, updateDownloadStatus, updateDownloadProgress, startDownload]);
  
  // [🎯 최적화] 반환값에 추가 정보 포함 (디버깅 및 모니터링용)
  return {
    downloadStatus: currentStatus,
    downloadProgress: currentProgress,
    startDownload,
    retryDownload, // [🔧 추가] 재시도 함수
    // [🎯 추가] 디버깅 정보 (개발 환경에서만 사용)
    ...(process.env.NODE_ENV === 'development' && {
      _debug: {
        themeId,
        isInitialized: isInitializedRef.current,
        hasContextStatus: !!contextDownloadStatus[themeId],
        statusData: currentStatusData
      }
    })
  };
};

export default useThemeDownload;